import { useCallback } from 'react';
import { usePageContext } from '@/context/page-context';
import { useSessionStorageState } from './use-session-storage-state';
import { useGeoLocation } from './use-geo-location';
import { useLocaleSuggestion } from './use-locale-suggestion';

const SESSION_STORAGE_KEY = 'locale_mismatch_dismissed';

/**
 * Detects whether the visitor's geo-located locale differs from the current
 * page locale and, if so, offers a suggestion to switch.
 *
 * The suggestion modal can be dismissed once per session (stored in sessionStorage).
 *
 * @returns {{
 *   showModal: boolean,
 *   suggestedLocale: import('@/enums/countries').Country | null,
 *   currentLocale: string,
 *   handleDismissModal: () => void,
 * }}
 */
export function useLocaleMismatch() {
	const pageContext = usePageContext();
	const [dismissed, setDismissed] = useSessionStorageState(SESSION_STORAGE_KEY, false);
	const { geoCountry } = useGeoLocation();

	// Use the current determined locale, or fallback to English if we have an unsupported/undetermined locale
	// e.g., a route without a locale prefix
	const currentLocale = pageContext.locale ?? 'en';
	const suggestedLocale = useLocaleSuggestion(geoCountry, currentLocale);

	const showModal = !dismissed && suggestedLocale !== null;

	const handleDismissModal = useCallback(() => {
		setDismissed(true);
	}, [setDismissed]);

	return {
		showModal,
		suggestedLocale,
		currentLocale,
		handleDismissModal,
	};
}
